<template>
  <UIModal
    v-model="isOpen"
    :fix-header="true"
    :front-blur="isInventoryOpen || isTeamModalOpen || isCancelModalOpen"
    @closed="handleExit"
  >
    <template #header>
      <div v-if="!(isInventoryOpen || isTeamModalOpen)" class="header-wrapper">
        {{ $t(`halloweenExpeditionModalTitle_${loc}`) }}
      </div>
    </template>

    <div v-if="expeditionState" class="expedition-modal-wrapper">
      <div ref="holderRef" class="pixi-background-holder" />

      <UIHalloweenExpeditionLogs
        :expedition-id="expeditionState.expeditionId"
        :fullscreen="isLogsOpen"
        :logs="logs"
        @click="handleToggleLogs"
      />

      <div class="expedition-timer">
        <img alt="Sand Clock" src="/img/icons/sandClock.png" />
        <div class="expedition-timer-text">
          <div class="expedition-timer-text-title">{{ $t('HalloweenExpeditionModalTimeSpent') }}</div>
          <div class="expedition-timer-text-time">
            <UIHalloweenCountdownTimer :target="expeditionState.startedAt" direction="forward" />
          </div>
        </div>
        <div class="expedition-timer-text">
          <div class="expedition-timer-text-title">{{ $t('HalloweenExpeditionModalTimeRemains') }}</div>
          <div class="expedition-timer-text-time">
            <UIHalloweenCountdownTimer :target="expeditionState.endsAt" />
          </div>
        </div>
      </div>

      <div class="expedition-nav-wrapper">
        <div class="expedition-nav">
          <HalloweenExpeditionNavButton icon="/img/icons/halloweenExpeditionLogIcon.png" @click="handleToggleLogs">
            {{ $t('halloweenNavigationButtonLog') }}
          </HalloweenExpeditionNavButton>
          <HalloweenExpeditionNavButton
            :badge="totalInventoryChange"
            icon="/img/icons/halloweenExpeditionInventoryIcon.png"
            @click="handleOpenInventory"
          >
            {{ $t('halloweenNavigationButtonInventory') }}
          </HalloweenExpeditionNavButton>
          <HalloweenExpeditionNavButton
            :badge="deadCharactersCounter"
            icon="/img/icons/halloweenExpeditionTeamIcon.png"
            @click="handleOpenTeamModal"
          >
            {{ $t('halloweenNavigationButtonTeam') }}
          </HalloweenExpeditionNavButton>
          <HalloweenExpeditionNavButton
            :disabled="expeditionState.returningHome || halloween.isVirtuallyCancelled(expeditionState.expeditionId)"
            hover-gradient="red"
            icon="/img/icons/halloweenExpeditionCancelIcon.png"
            @click="handleTerminate"
          >
            {{ $t('halloweenNavigationButtonTerminate') }}
          </HalloweenExpeditionNavButton>
        </div>
      </div>
    </div>
  </UIModal>
  <HalloweenCancelExpeditionModal
    v-model="isCancelModalOpen"
    :founded-items="expeditionState?.inventory || []"
    :is-interrupt-processing="isInterruptProcessing"
    :time-of-return="returnExpeditionTime"
    @interrupt="handleInterrupt"
  />
  <HalloweenInventory v-model="isInventoryOpen" :inventory="expeditionState?.inventory || []" />
  <HalloweenExpeditionTeamModal
    v-if="expeditionState"
    v-model="isTeamModalOpen"
    :characters="expeditionState.characters"
  />
</template>
<script lang="ts" setup>
import halloweenCharacterFactory from '~/components/Halloween/lib/halloween-character-factory';
import initPixiApplication, { type HalloweenModalApplication } from '~/components/Halloween/lib/init-pixi-application';
import HalloweenCancelExpeditionModal from '~/components/Halloween/modal/HalloweenCancelExpeditionModal.vue';
import HalloweenExpeditionTeamModal from '~/components/Halloween/modal/HalloweenExpeditionTeamModal.vue';
import UIHalloweenCountdownTimer from '~/components/Halloween/ui/UIHalloweenCountdownTimer.vue';
import UIHalloweenExpeditionLogs from '~/components/Halloween/ui/UIHalloweenExpeditionLogs.vue';
import UIModal from '~/shared/components/UIModal.vue';
import type { ExpeditionLog } from '~/types/halloween/expedition';
import { Buildings } from '~/utils/constants';
import { HalloweenInventory } from '~/widgets/halloween';

defineProps<{
  logs: ExpeditionLog[];
}>();

const totalInventoryChange = computed(() => {
  if (!expeditionState.value) return undefined;
  const totalItemSlots = expeditionState.value.inventory.map((i) => i.quantity);
  if (totalItemSlots.length === 0) return undefined;
  if (totalItemSlots.length === 1) return totalItemSlots[0];

  return totalItemSlots.reduce((a, b) => a + b, 0);
});

const deadCharactersCounter = computed(() => {
  if (!expeditionState.value) return undefined;
  return expeditionState.value.characters.filter((c) => !c.alive).length * -1;
});

const emit = defineEmits<{
  (event: 'closed'): void;
  (event: 'open'): void;
}>();

const isOpen = defineModel<boolean>({ default: false });

const loc = defineModel<'forest' | 'cave'>('location', {
  default: 'forest',
  required: true
});

const halloween = useHalloweenStore();
const mainStore = useMainStore();
const expeditionState = computed(() =>
  halloween.$state.statefulUserExpeditions.find((exp) => exp.location === loc.value)
);

const isInventoryOpen = ref(false);
const handleOpenInventory = () => (isInventoryOpen.value = true);

const isLogsOpen = ref(false);
const handleToggleLogs = () => (isLogsOpen.value = !isLogsOpen.value);

const isTeamModalOpen = ref(false);
const handleOpenTeamModal = () => (isTeamModalOpen.value = true);

const holderRef = ref<HTMLDivElement | null>(null);
const app = ref<HalloweenModalApplication | null>(null);
const isCancelModalOpen = ref(false);
const isInterruptProcessing = ref(false);

const handleTerminate = () => {
  isCancelModalOpen.value = true;
};

const handleExit = () => {
  isLogsOpen.value = false;
  emit('closed');
  setTimeout(() => {
    app.value?.destroy();
    app.value = null;
  }, 180);
};

const returnExpeditionTime = computed(() => {
  if (!expeditionState.value) return '';
  return expeditionState.value.returnsAt;
});

const handleInterrupt = async () => {
  if (isInterruptProcessing.value) return;
  if (!expeditionState.value) return;

  const expeditionId = expeditionState.value.expeditionId;

  isInterruptProcessing.value = true;

  console.log(`[Expedition Modal] Attempting to interrupt expedition ID: ${expeditionId}`);

  try {
    await halloween.interruptExpedition(expeditionId);
    await halloween.updateUserExpeditions();
  } catch (error) {
    console.error('[Expedition Modal] Error during interruption transaction:', error);
  } finally {
    isCancelModalOpen.value = false;
    isInterruptProcessing.value = false;
  }
};

watch(holderRef, (newVal) => {
  if (!newVal) return;

  if (newVal.childNodes.length > 0) return;

  if (!app.value) {
    initPixiApplication(newVal, loc.value).then((res) => {
      app.value = res;
      newVal.appendChild(res.view as any);
    });
  } else {
    newVal.appendChild(app.value.view as any);
  }
});

watch(
  [app, loc],
  ([appInst, loc]) => {
    if (!appInst) return;
    appInst.setLocation(loc);
  },
  { immediate: true }
);

watch(
  [isOpen, loc],
  ([modalOpen, currentLocation]: [boolean, 'forest' | 'cave']) => {
    if (modalOpen && currentLocation) {
      const buildingKey = currentLocation === 'forest' ? Buildings.Forest : Buildings.Cave;
      mainStore.openedBuilding = buildingKey;
    } else if (!modalOpen) {
      mainStore.openedBuilding = null;
    }
  },
  { immediate: true }
);

whenever(isOpen, () => {
  emit('open');

  if (expeditionState.value && halloween.isExpeditionFinished(expeditionState.value.expeditionId)) {
    isOpen.value = false;
    halloween.$state.isExpeditionCompletedModalOpen = true;
    emit('closed');
  }
});

watch(
  [expeditionState, app],
  ([newExpeditionState]) => {
    if (!newExpeditionState || !app.value) return;

    if (halloween.isExpeditionFinished(newExpeditionState.expeditionId)) {
      isOpen.value = false;
      halloween.$state.isExpeditionCompletedModalOpen = true;
      emit('closed');
      return;
    }

    app.value.setDirection(newExpeditionState.returningHome ? 'left' : 'right');

    halloweenCharacterFactory.reset();
    halloweenCharacterFactory.$updateCharactersPositions();
    halloweenCharacterFactory.createMany(newExpeditionState.characters.map((character) => character.characterType));
    newExpeditionState.characters.forEach((character) => {
      const currentHealth = character.currentHealth ?? character.health;
      halloweenCharacterFactory
        .get(character.characterType)
        .createCharacterSprite()
        .setWalkAnimation(newExpeditionState.returningHome ? 'left' : 'right')
        .setVisible(true)
        .setHealth(Math.round((currentHealth / character.health) * 100));
    });

    halloweenCharacterFactory.$updateCharactersPositions();
  },
  { immediate: true }
);

onUnmounted(() => {
  app.value?.destroy();
});
</script>
<style scoped>
.expedition-modal-wrapper {
  width: 900px;
  max-width: 100%;
  height: 650px;
  position: relative;
  display: block;
  border-radius: 0.75rem;
  overflow: hidden;

  @media screen and (max-width: 640px) {
    height: 100%;
  }
}

.pixi-background-holder {
  width: 100%;
  max-width: 100%;
  height: 100%;
  position: relative;
  display: block;

  @media screen and (max-width: 640px) {
    max-height: 60vh;
  }
}

.expedition-nav {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: fit-content;
  bottom: 30px;
  left: 0;
  z-index: 110;
}

.expedition-nav-wrapper {
  position: relative;
  width: 100%;
  bottom: 0;
  height: 0;

  @media screen and (max-width: 640px) {
    position: absolute;
    bottom: 0;
  }
}

.header-wrapper {
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  font-family: Grenze, serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 30px;
  text-align: center;
  vertical-align: middle;
  color: #fff;
}

.expedition-timer {
  top: 85px;
  right: 24px;
  position: absolute;
  display: flex;
  gap: 20px;

  background: rgba(16, 43, 54, 0.5);
  border: 1px solid #10364a;
  backdrop-filter: blur(5px);
  border-radius: 10px;
  padding: 10px 34px;
  margin-left: 20px;

  & > img {
    position: absolute;
    left: -40px;
    top: calc(50% - 38px);

    width: 76px;
    height: 76px;
    min-width: 48px;
  }

  @media screen and (max-width: 640px) {
    right: 50%;
    transform: translateX(50%);
    top: 80px;
  }
}

.expedition-timer-text {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;

  .expedition-timer-text-title {
    font-family: Eczar, serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 100%;
    vertical-align: middle;
    color: #8a8689;
    text-align: center;
  }

  .expedition-timer-text-time {
    font-family: Eczar, serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 100%;
    vertical-align: middle;
    color: white;

    white-space: nowrap;

    @media screen and (max-width: 640px) {
      font-size: 16px;
    }
  }
}
</style>
